import { useState } from 'react';
import { Alert, Badge, Code, Group, Select, Table, Text, TextInput } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import { useAudit } from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { fmtDate } from '../theme/format';

const ACTIONS = [
  'message.created', 'broadcast.sent', 'integration.updated',
  'integration.credentials_read', 'api_key.created', 'api_key.revoked',
  'profile.deleted', 'profile.exported', 'preference.updated',
  'suppression.added', 'suppression.removed', 'journey.activated',
  'template.updated', 'member.invited', 'member.removed',
];

/** Actions that read or change something sensitive get a coloured badge. */
const SENSITIVE = new Set([
  'integration.credentials_read', 'profile.exported', 'profile.deleted',
  'api_key.created', 'suppression.removed', 'member.invited', 'member.removed',
]);

export function AuditPage() {
  const [action, setAction] = useState<string | null>(null);
  const [actorId, setActorId] = useState('');
  const { data, isLoading, error } = useAudit({
    action: action ?? undefined,
    actor_id: actorId || undefined,
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every mutating action, plus reads of anything sensitive. Append-only: the application role has UPDATE and DELETE revoked, so nothing here can be rewritten after the fact."
      />

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} mb="md">
        <Text size="sm">
          This is what answers &ldquo;who sent that broadcast to 8,000 people?&rdquo;.
          The actor&apos;s email is stored alongside their id, so the log stays
          readable after an account is deleted.
        </Text>
      </Alert>

      <Group mb="md">
        <Select
          size="xs" w={260} clearable searchable
          placeholder="Filter by action"
          data={ACTIONS}
          value={action}
          onChange={setAction}
        />
        <TextInput
          size="xs" w={280}
          placeholder="Filter by actor id"
          value={actorId}
          onChange={(e) => setActorId(e.currentTarget.value)}
        />
      </Group>

      <DataState
        loading={isLoading}
        error={error}
        empty={!data?.length}
        emptyTitle="Nothing recorded yet"
        emptyDescription="Actions appear here from the moment the project is created."
      >
        <Table.ScrollContainer minWidth={780}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>When</Table.Th>
                <Table.Th>Actor</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Resource</Table.Th>
                <Table.Th>Detail</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td>
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(e.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Badge size="xs" variant="light" color="gray">{e.actor_type}</Badge>
                      <Text size="sm">{e.actor_label ?? e.actor_id.slice(0, 8)}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={SENSITIVE.has(e.action) ? 'orange' : 'gray'}
                    >
                      {e.action}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Code style={{ fontSize: 11 }}>{e.resource}</Code></Table.Td>
                  <Table.Td maw={260}>
                    {Object.keys(e.metadata ?? {}).length > 0 && (
                      <Code style={{ fontSize: 11 }}>
                        {JSON.stringify(e.metadata).slice(0, 80)}
                      </Code>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </DataState>
    </>
  );
}
